import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Lock, UploadCloud, LogOut, Trash2, Save, ArrowLeft,
  Download, Activity, CalendarDays, FileDown, Link2, KeyRound,
} from "lucide-react";
import { api, formatApiError, formatBytes } from "@/lib/api";

const LoginCard = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("cyrion_admin_token", data.access_token);
      toast.success("Welcome back, commander");
      onLogin(data.user);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="glass-panel rounded-3xl p-8 sm:p-10 w-full max-w-md"
      data-testid="admin-login-form"
    >
      <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/25 flex items-center justify-center mb-6">
        <Lock className="w-6 h-6 text-gold" />
      </div>
      <h1 className="font-display text-2xl font-bold text-slate-100 mb-1">Vault Control</h1>
      <p className="text-sm text-slate-500 mb-8">Restricted to the Cyrion operator.</p>
      <label className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Username</label>
      <input
        type="text"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid="admin-login-email-input"
        className="w-full bg-ink/70 border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 text-sm text-slate-100 outline-none transition-colors mb-5"
        placeholder="admin"
      />
      <label className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Password</label>
      <input
        type="password"
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        data-testid="admin-login-password-input"
        className="w-full bg-ink/70 border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 text-sm text-slate-100 outline-none transition-colors mb-8"
        placeholder="••••••••••"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="admin-login-button"
        className="w-full bg-gold text-ink font-display font-bold text-sm py-3.5 rounded-full hover:bg-gold-soft hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50"
      >
        {loading ? "Unlocking…" : "Unlock Console"}
      </button>
    </motion.form>
  );
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="glass-panel rounded-2xl p-6" data-testid={testid}>
    <div className="flex items-center gap-3 mb-3">
      <Icon className="w-4 h-4 text-mint" />
      <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">{label}</p>
    </div>
    <p className="font-display text-3xl font-extrabold gold-text">{value}</p>
  </div>
);

const Dashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState("");
  const [counter, setCounter] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fileUrl, setFileUrl] = useState("");
  const [urlVersion, setUrlVersion] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
      setVersion(data.release.version || "");
      setCounter(String(data.total_downloads ?? 0));
    } catch (err) {
      if (err?.response?.status === 401) onLogout();
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async () => {
    if (!file) { toast.error("Choose an installer file first"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("version", version || "1.0.0");
      await api.post("/admin/upload", form);
      toast.success("New installer deployed live");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const saveUrl = async () => {
    if (!fileUrl.trim()) { toast.error("Paste the file URL first"); return; }
    setSavingUrl(true);
    try {
      await api.post("/admin/release-url", { url: fileUrl.trim(), version: urlVersion || "1.0.0" });
      toast.success("Download now serves the linked URL");
      setFileUrl("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingUrl(false);
    }
  };

  const saveCounter = async () => {
    try {
      await api.post("/admin/counter", { count: parseInt(counter || "0", 10) });
      toast.success("Counter updated");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changePassword = async () => {
    if (!curPw || !newPw) { toast.error("Fill in both password fields"); return; }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Password updated — use it on your next login");
      setCurPw("");
      setNewPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setChangingPw(false);
    }
  };

  const clearLogs = async () => {
    try {
      await api.post("/admin/logs/clear");
      toast.success("Download logs cleared");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const rel = stats?.release || {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="w-full max-w-5xl"
      data-testid="admin-dashboard"
    >
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-100">Vault Control</h1>
          <p className="text-sm text-slate-500 mt-1">Deploy installers, watch the counter.</p>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-slate-400 border border-white/10 hover:border-gold/40 hover:text-gold rounded-full px-4 py-2 transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" />
          Logout
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <StatCard icon={Download} label="Total downloads" value={(stats?.total_downloads ?? 0).toLocaleString()} testid="stat-total-downloads" />
        <StatCard icon={Activity} label="Last 24 hours" value={(stats?.last_24h ?? 0).toLocaleString()} testid="stat-last-24h" />
        <StatCard icon={CalendarDays} label="Last 7 days" value={(stats?.last_7d ?? 0).toLocaleString()} testid="stat-last-7d" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="space-y-6">
          <div className="glass-panel rounded-3xl p-8">
            <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">Option 1 — Upload from your PC</h2>
            <p className="text-xs text-slate-500 mb-6">Any file type, signed or unsigned — live the moment upload finishes.</p>
            <input
              ref={fileRef}
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="admin-upload-file-input"
              className="w-full text-xs text-slate-400 file:mr-4 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:bg-gold/10 file:text-gold file:font-mono file:text-[11px] hover:file:bg-gold/20 file:cursor-pointer mb-4"
            />
            <label className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Version tag</label>
            <input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              data-testid="admin-upload-version-input"
              className="w-full bg-ink/70 border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 text-sm text-slate-100 outline-none transition-colors mb-6"
              placeholder="1.0.0"
            />
            <button
              onClick={upload}
              disabled={uploading}
              data-testid="admin-upload-button"
              className="flex items-center gap-2 bg-gold text-ink font-display font-bold text-sm px-6 py-3 rounded-full hover:bg-gold-soft transition-all duration-300 disabled:opacity-50"
            >
              <UploadCloud className="w-4 h-4" />
              {uploading ? "Uploading…" : "Deploy Installer"}
            </button>
          </div>

          <div className="glass-panel rounded-3xl p-8">
            <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">Option 2 — Link a file URL</h2>
            <p className="text-xs text-slate-500 mb-6">Point downloads at a file hosted anywhere. Visitors just press download — the counter still tracks every click.</p>
            <label className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Direct file URL</label>
            <input
              type="url"
              value={fileUrl}
              onChange={(e) => setFileUrl(e.target.value)}
              data-testid="admin-url-input"
              className="w-full bg-ink/70 border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 text-sm text-slate-100 outline-none transition-colors mb-4"
              placeholder="https://example.com/Cyrion-Wallet-Setup.exe"
            />
            <label className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Version tag</label>
            <input
              value={urlVersion}
              onChange={(e) => setUrlVersion(e.target.value)}
              data-testid="admin-url-version-input"
              className="w-full bg-ink/70 border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 text-sm text-slate-100 outline-none transition-colors mb-6"
              placeholder="1.0.0"
            />
            <button
              onClick={saveUrl}
              disabled={savingUrl}
              data-testid="admin-url-save-btn"
              className="flex items-center gap-2 border border-gold/40 text-gold font-display font-bold text-sm px-6 py-3 rounded-full hover:bg-gold/10 transition-all duration-300 disabled:opacity-50"
            >
              <Link2 className="w-4 h-4" />
              {savingUrl ? "Checking link…" : "Use This URL"}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass-panel rounded-3xl p-8">
            <h2 className="font-display text-lg font-semibold text-slate-100 mb-4">Current release</h2>
            {rel.available ? (
              <div className="space-y-2 font-mono text-xs text-slate-400" data-testid="admin-release-info">
                <p className="flex items-center gap-2">
                  {rel.source_type === "url" ? <Link2 className="w-3.5 h-3.5 text-gold" /> : <FileDown className="w-3.5 h-3.5 text-mint" />}
                  {rel.filename}
                  <span className={`text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full border ${rel.source_type === "url" ? "border-gold/40 text-gold" : "border-mint/40 text-mint"}`} data-testid="admin-release-source-badge">
                    {rel.source_type === "url" ? "Linked URL" : "Uploaded"}
                  </span>
                </p>
                <p>v{rel.version} · {formatBytes(rel.size)}</p>
                {rel.sha256 && <p className="truncate">SHA-256: {rel.sha256}</p>}
              </div>
            ) : (
              <p className="text-sm text-slate-500" data-testid="admin-release-empty">No installer live yet. Upload your first build.</p>
            )}
          </div>
          <div className="glass-panel rounded-3xl p-8">
            <h2 className="font-display text-lg font-semibold text-slate-100 mb-4">Counter override</h2>
            <div className="flex gap-3">
              <input
                type="number"
                min="0"
                value={counter}
                onChange={(e) => setCounter(e.target.value)}
                data-testid="admin-counter-input"
                className="flex-1 bg-ink/70 border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 text-sm text-slate-100 outline-none transition-colors"
              />
              <button
                onClick={saveCounter}
                data-testid="admin-counter-save-btn"
                className="flex items-center gap-2 border border-gold/40 text-gold font-display font-bold text-sm px-5 py-3 rounded-full hover:bg-gold/10 transition-colors"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>

          <div className="glass-panel rounded-3xl p-8">
            <h2 className="font-display text-lg font-semibold text-slate-100 mb-1">Change password</h2>
            <p className="text-xs text-slate-500 mb-5">Takes effect immediately and survives restarts.</p>
            <label className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">Current password</label>
            <input
              type="password"
              value={curPw}
              onChange={(e) => setCurPw(e.target.value)}
              data-testid="admin-current-password-input"
              className="w-full bg-ink/70 border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 text-sm text-slate-100 outline-none transition-colors mb-4"
              placeholder="••••••••"
            />
            <label className="block font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 mb-2">New password</label>
            <input
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              data-testid="admin-new-password-input"
              className="w-full bg-ink/70 border border-white/10 focus:border-gold/50 rounded-xl px-4 py-3 text-sm text-slate-100 outline-none transition-colors mb-6"
              placeholder="••••••••"
            />
            <button
              onClick={changePassword}
              disabled={changingPw}
              data-testid="admin-change-password-btn"
              className="flex items-center gap-2 border border-gold/40 text-gold font-display font-bold text-sm px-6 py-3 rounded-full hover:bg-gold/10 transition-all duration-300 disabled:opacity-50"
            >
              <KeyRound className="w-4 h-4" />
              {changingPw ? "Updating…" : "Update Password"}
            </button>
          </div>
        </div>
      </div>

      <div className="glass-panel rounded-3xl p-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-lg font-semibold text-slate-100">Recent downloads</h2>
          <button
            onClick={clearLogs}
            data-testid="admin-clear-logs-btn"
            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 hover:text-red-400 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Clear logs
          </button>
        </div>
        {stats?.recent_events?.length ? (
          <div className="max-h-64 overflow-y-auto space-y-2" data-testid="admin-download-logs">
            {stats.recent_events.map((ev) => (
              <div key={ev.id} className="flex justify-between font-mono text-[11px] text-slate-400 border-b border-white/5 pb-2">
                <span>{new Date(ev.at).toLocaleString()}</span>
                <span className="text-slate-600">{ev.ip}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500" data-testid="admin-logs-empty">No downloads recorded yet.</p>
        )}
      </div>
    </motion.div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const check = async () => {
      if (!localStorage.getItem("cyrion_admin_token")) { setUser(null); return; }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        localStorage.removeItem("cyrion_admin_token");
        setUser(null);
      }
    };
    check();
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("cyrion_admin_token");
    setUser(null);
  };

  return (
    <div className="min-h-screen bg-ink grain relative flex flex-col items-center justify-center px-4 py-16" data-testid="admin-page">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-gold/[0.06] blur-[120px] pointer-events-none" />
      <Link
        to="/"
        data-testid="admin-back-link"
        className="absolute top-6 left-6 flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-slate-500 hover:text-gold transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to site
      </Link>
      {user === undefined ? null : user ? (
        <Dashboard onLogout={logout} />
      ) : (
        <LoginCard onLogin={setUser} />
      )}
    </div>
  );
}
